import React, { createContext, useCallback, useMemo, useState } from "react";

import {
  GetRv2UsersResponse,
  GetSubmissionListResponse,
  GetSubmissionResponsePortal,
  GetSurveyResponse,
  SubmissionSummary,
  SurveyConfiguration,
  getOrgConfigPublic,
  getOrgUsers,
  getSubmissionPortal,
  getSurvey,
  listSubmissionRegistry,
  listSubmissionReport,
} from "@rubi/api-generated";

import { useDevEnvironment } from "@/contexts/DevContext";

import { SubmissionSortField } from "./submissionSort";

export interface OnlineReportingState {
  reports: SubmissionSummary[];
  reportTypeNames: string[];
  registryTypeNames: string[];
  reportConfigurations: SurveyConfiguration[] | null;
  totalReportsCount: number;
  fetchLatestReports: (
    organizationID: string,
    timeCreatedMin?: string,
    timeCreatedMax?: string,
    timeUpdatedMin?: string,
    timeUpdatedMax?: string,
    status?: string,
    trackingNumber?: string,
    limit?: number,
    offset?: number,
    reportType?: string,
    search?: string,
    assignedTo?: string,
    registries?: boolean,
    image?: string | null,
    imageSimilarity?: number | null,
    sortOrder?: "desc" | "asc" | null,
    region?: string,
    sortBy?: SubmissionSortField | null,
  ) => Promise<void>;
  fetchReportConfigurations: (organizationID: string) => Promise<void>;
  getSubmissionForID: (
    submissionID: string,
    version?: string,
  ) => Promise<GetSubmissionResponsePortal | null>;
  getSurveyForID: (surveyID: string) => Promise<GetSurveyResponse | null>;
  searchText: string;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  filtersVisible: boolean;
  setFiltersVisible: React.Dispatch<React.SetStateAction<boolean>>;
  searchView: "list" | "map" | "card";
  setSearchView: React.Dispatch<React.SetStateAction<"list" | "map" | "card">>;
  reportTablePage: number;
  setReportTablePage: React.Dispatch<React.SetStateAction<number>>;
  rv2Users: GetRv2UsersResponse | null;
  fetchRv2Users: (organizationID: string) => Promise<void>;
  imageSearchID: string | null;
  setImageSearchID: React.Dispatch<React.SetStateAction<string | null>>;
  imageAttachmentId: string | null;
  setImageAttachmentId: React.Dispatch<React.SetStateAction<string | null>>;
  imageSubmissionId: string | null;
  setImageSubmissionId: React.Dispatch<React.SetStateAction<string | null>>;
  currentSearchImageData: string | null;
  setCurrentSearchImageData: React.Dispatch<
    React.SetStateAction<string | null>
  >;
}

// Create the context
export const OnlineReportingContext = createContext<OnlineReportingState>({
  searchText: "",
  setSearchText: () => {},
  imageSearchID: null,
  setImageSearchID: () => {},
  imageAttachmentId: null,
  setImageAttachmentId: () => {},
  imageSubmissionId: null,
  setImageSubmissionId: () => {},
  currentSearchImageData: null,
  setCurrentSearchImageData: () => {},
  reports: [],
  reportTypeNames: [],
  registryTypeNames: [],
  reportConfigurations: null,
  totalReportsCount: 0,
  fetchLatestReports: async () => {},
  fetchReportConfigurations: async () => {},
  getSubmissionForID: async () => null,
  getSurveyForID: async () => null,
  filtersVisible: false,
  setFiltersVisible: () => {},
  searchView: "list",
  setSearchView: () => {},
  reportTablePage: 0,
  setReportTablePage: () => {},
  rv2Users: null,
  fetchRv2Users: async () => {},
});

const OnlineReportingProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { isDevEnv } = useDevEnvironment();
  const [reports, setReports] = useState<SubmissionSummary[]>([]);
  const [reportTypeNames, setReportTypeNames] = useState<string[]>([]);
  const [registryTypeNames, setRegistryTypeNames] = useState<string[]>([]);
  const [reportConfigurations, setReportConfigurations] = useState<
    SurveyConfiguration[] | null
  >(null);
  const [totalReportsCount, setTotalReportsCount] = useState(0);

  const [rv2Users, setRv2Users] = useState<GetRv2UsersResponse | null>(null);

  // search
  const [searchText, setSearchText] = useState<string>("");
  const [imageSearchID, setImageSearchID] = useState<string | null>(null);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [searchView, setSearchView] = useState<"list" | "map" | "card">("list");
  const [reportTablePage, setReportTablePage] = useState(0);
  const [imageAttachmentId, setImageAttachmentId] = useState<string | null>(
    null,
  );
  const [imageSubmissionId, setImageSubmissionId] = useState<string | null>(
    null,
  );
  const [currentSearchImageData, setCurrentSearchImageData] = useState<
    string | null
  >(null);

  const fetchLatestReports = useCallback(
    async (
      organizationID: string,
      timeCreatedMin?: string,
      timeCreatedMax?: string,
      timeUpdatedMin?: string,
      timeUpdatedMax?: string,
      status?: string,
      trackingNumber?: string,
      limit?: number,
      offset?: number,
      reportType?: string,
      search?: string,
      assignedTo?: string,
      registries?: boolean,
      imageSearch?: string | null,
      imageSimilarity?: number | null,
      sortOrder?: "desc" | "asc" | null,
      region?: string,
      sortBy?: SubmissionSortField | null,
    ) => {
      let fullList: GetSubmissionListResponse;
      if (registries ?? false) {
        fullList = (
          await listSubmissionRegistry({
            query: {
              time_created_min: timeCreatedMin,
              time_created_max: timeCreatedMax,
              time_updated_min: timeUpdatedMin,
              time_updated_max: timeUpdatedMax,
              status: status,
              source: null,
              first_name: null,
              last_name: null,
              email: null,
              phone_number: null,
              secondary_phone_number: null,
              incident_number: null,
              tracking_number: trackingNumber ?? null,
              limit,
              offset,
              survey_name:
                reportType && reportType?.trim().length > 0 ? reportType : null,
              search: search && search?.trim().length > 0 ? searchText : null,
              assigned_to: assignedTo ?? null,
              image_search_id: imageSearch ?? imageSearchID ?? null,
              image_attachment_id: imageAttachmentId ?? null,
              image_submission_id: imageSubmissionId ?? null,
              image_similarity: imageSimilarity ? imageSimilarity / 100 : null,
              sort_order: sortOrder ?? null,
              sort_by: sortBy ?? null,
              org_id: organizationID,
              region_id: region ?? null,
            } as any,
          })
        ).data!;
      } else {
        fullList = (
          await listSubmissionReport({
            query: {
              time_created_min: timeCreatedMin,
              time_created_max: timeCreatedMax,
              time_updated_min: timeUpdatedMin,
              time_updated_max: timeUpdatedMax,
              status: status,
              source: null,
              first_name: null,
              last_name: null,
              email: null,
              phone_number: null,
              secondary_phone_number: null,
              incident_number: null,
              tracking_number: trackingNumber ?? null,
              limit,
              offset,
              survey_name:
                reportType && reportType?.trim().length > 0 ? reportType : null,
              search: search && search?.trim().length > 0 ? searchText : null,
              assigned_to: assignedTo ?? null,
              image_search_id: imageSearch ?? imageSearchID ?? null,
              image_attachment_id: imageAttachmentId ?? null,
              image_submission_id: imageSubmissionId ?? null,
              image_similarity: imageSimilarity ? imageSimilarity / 100 : null,
              sort_order: sortOrder ?? null,
              sort_by: sortBy ?? null,
              org_id: organizationID,
              region_id: region ?? null,
            } as any,
          })
        ).data!;
      }

      setReports(fullList.submissions);
      setTotalReportsCount(fullList.total_count);
    },
    [searchText, imageSearchID, imageAttachmentId, imageSubmissionId],
  );

  const fetchReportConfigurations = useCallback(
    async (organizationID: string) => {
      const publicConfig = (
        await getOrgConfigPublic({ path: { org_id: organizationID } })
      ).data!;
      setReportConfigurations(publicConfig.surveys);
      const names = publicConfig.surveys
        .filter((survey) => !survey.is_registry)
        .map((survey) => survey.name);
      setReportTypeNames(names);
      const registryNames = publicConfig.surveys
        .filter((survey) => survey.is_registry)
        .map((survey) => survey.name);
      setRegistryTypeNames(registryNames);
    },
    [],
  );

  const getSubmissionForID = useCallback(
    async (submissionID: string, version?: string) => {
      const result = (
        await getSubmissionPortal({
          path: { submission_id: submissionID },
          query: { version: version ?? "latest" },
        })
      ).data!;
      return result;
    },
    [],
  );

  const getSurveyForID = useCallback(async (surveyID: string) => {
    const result = (await getSurvey({ path: { survey_id: surveyID } })).data!;
    return result;
  }, []);

  const fetchRv2Users = useCallback(async (organizationID: string) => {
    const usersList = (await getOrgUsers({ path: { org_id: organizationID } }))
      .data!;
    setRv2Users(usersList);
  }, []);

  const value = useMemo<OnlineReportingState>(
    () => ({
      reports,
      reportTypeNames,
      registryTypeNames,
      reportConfigurations,
      totalReportsCount,
      fetchLatestReports,
      fetchReportConfigurations,
      getSubmissionForID,
      getSurveyForID,
      searchText,
      setSearchText,
      filtersVisible,
      setFiltersVisible,
      searchView,
      setSearchView,
      reportTablePage,
      setReportTablePage,
      rv2Users,
      fetchRv2Users,
      imageSearchID,
      setImageSearchID,
      imageAttachmentId,
      setImageAttachmentId,
      imageSubmissionId,
      setImageSubmissionId,
      currentSearchImageData,
      setCurrentSearchImageData,
    }),
    [
      reports,
      reportTypeNames,
      registryTypeNames,
      reportConfigurations,
      totalReportsCount,
      fetchLatestReports,
      fetchReportConfigurations,
      getSubmissionForID,
      getSurveyForID,
      searchText,
      filtersVisible,
      searchView,
      reportTablePage,
      rv2Users,
      fetchRv2Users,
      imageSearchID,
      imageAttachmentId,
      imageSubmissionId,
      currentSearchImageData,
    ],
  );

  return (
    <OnlineReportingContext.Provider value={value}>
      {children}
    </OnlineReportingContext.Provider>
  );
};

export default OnlineReportingProvider;
